import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { MagnifyingGlass, X, Plus, Scales } from "@phosphor-icons/react";

/**
 * Debounced case typeahead. Searches /cases?q= by case_number, plaintiff,
 * defendant, judge, in_re_case_name. Same UX shell as Customer/UserSearchInput.
 *
 * Props mirror CustomerSearchInput.
 */
export default function CaseSearchInput({
  value,
  valueLabel,
  onSelect,
  onClear,
  onCreateNew,
  placeholder = "Search by case #, plaintiff, defendant, or judge…",
  testid = "case-search",
  autoFocus = false,
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open) return;
    debounceRef.current = setTimeout(() => {
      setBusy(true);
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}&limit=12` : "?limit=12";
      api.get(`/cases${params}`)
        .then((r) => {
          const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          setResults(items.slice(0, 12));
        })
        .catch(() => setResults([]))
        .finally(() => setBusy(false));
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (c) => {
    onSelect && onSelect(c);
    setQ(""); setResults([]); setOpen(false);
  };

  // Compose a one-line summary for a case row.
  const summarize = (c) => {
    const parties = [c.plaintiff, c.defendant].filter(Boolean).join(" v. ");
    const tail = [c.judge ? `Judge ${c.judge}` : null, parties || null].filter(Boolean).join(" · ");
    return tail || "—";
  };

  if (value && valueLabel) {
    return (
      <div ref={rootRef} className="flex items-center gap-2" data-testid={`${testid}-chip`}>
        <div className="flex-1 px-3 py-2 rounded-md border border-border bg-card flex items-center gap-2 min-h-[40px]">
          <Scales size={14} className="text-muted-foreground" />
          <span className="text-sm truncate">{valueLabel}</span>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => onClear && onClear()}
          data-testid={`${testid}-clear`}
          title="Clear case"
        >
          <X size={14} /> CLEAR
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            className="input pl-9"
            placeholder={placeholder}
            value={q}
            autoFocus={autoFocus}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            data-testid={`${testid}-input`}
          />
        </div>
        {onCreateNew && (
          <button
            type="button"
            className="btn btn-secondary btn-sm whitespace-nowrap"
            onClick={() => { setOpen(false); onCreateNew(); }}
            data-testid={`${testid}-new-btn`}
          >
            <Plus size={12} /> NEW
          </button>
        )}
      </div>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 surface border border-border rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${testid}-results`}
        >
          {busy && results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              No cases match{q ? ` "${q}"` : ""}.
              {onCreateNew && (
                <button
                  type="button"
                  className="ml-2 text-primary underline"
                  onClick={() => { setOpen(false); onCreateNew(); }}
                  data-testid={`${testid}-empty-create`}
                >
                  Create new case
                </button>
              )}
            </div>
          ) : (
            results.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => pick(c)}
                className="w-full text-left px-3 py-2 hover:bg-accent flex flex-col gap-0.5 border-b border-border last:border-b-0"
                data-testid={`${testid}-result-${c.id}`}
              >
                <span className="text-sm font-medium">
                  {c.case_number || c.in_re_case_name || c.id.slice(0, 8)}
                </span>
                <span className="text-xs text-muted-foreground truncate">{summarize(c)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
